<template>
  <div class="bg-bg-cream">
    <!-- Hero Section -->
    <section
      class="relative min-h-[75svh] pb-4 pt-20 md:pb-20 md:pt-40 flex flex-col"
    >
      <!-- Background Image -->
      <div class="absolute inset-0 pointer-events-none">
        <img
          :src="getLocalizedImage('/images/hero-couple.webp')"
          alt=""
          class="w-full h-full object-cover object-[75%_50%] sm:object-center"
        />
        <div
          class="absolute inset-0 gradient-overlay"
        ></div>
      </div>

      <!-- Hero Content -->
      <div
        class="relative max-w-7xl w-full mx-auto px-4 md:px-0 flex flex-col items-start gap-4 sm:gap-12 mt-auto md:mt-0 space-y-4 sm:space-y-0"
      >
        <div class="flex flex-col gap-4 text-white w-full md:w-1/2">
          <h1
            class="text-2xl md:text-display-lg font-semibold leading-tight tracking-wide whitespace-pre-line"
          >
            {{ $t('landing.hero.title') }}
          </h1>
          <p class="text-md md:text-xl">
            {{ $t('landing.hero.subtitle') }}
          </p>
        </div>
        <div
          class="flex flex-col gap-2 items-center justify-center w-full sm:w-auto"
        >
          <Button variant="primary" size="md" class="md:px-8 md:py-3 md:text-md w-full sm:w-80"> {{ $t('landing.hero.cta') }} </Button>
          <p
            class="text-center text-white font-medium text-sm sm:text-md"
          >
            {{ $t('landing.hero.subtext') }}
          </p>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="bg-bg-cream py-10">
      <div
        class="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 flex flex-col gap-10 md:gap-20"
      >
        <div
          class="max-w-[1080px] mx-auto w-full flex flex-col gap-10 md:gap-20"
        >
          <!-- Section Header -->
          <div
            class="border-b-2 border-pruta-green flex flex-col md:flex-row gap-4 md:gap-2 md:items-end pb-6"
          >
            <h2
              class="font-medium text-3xl md:text-display-md text-pruta-green md:w-[536px] whitespace-pre-line"
            >
              {{ $t('landing.how_it_works.title') }}
            </h2>
            <p class="flex-1 font-medium text-sm md:text-base text-gray-darker">
              {{ $t('landing.how_it_works.description') }}
            </p>
          </div>

          <!-- How It Works Content -->
          <div class="flex flex-col gap-6 md:gap-10">
            <div
              class="flex flex-col md:flex-row gap-4 md:items-center md:justify-between"
            >
              <h3
                class="font-semibold text-2xl md:text-display-sm text-pruta-green"
              >
                {{ $t('landing.how_it_works.heading') }}
              </h3>
              <div
                class="bg-pruta-green border border-gray-light rounded-full p-1 flex gap-1 "
              >
                <button
                  @click="activeTab = 'homeowner'"
                  :class="[
                    'rounded-full px-4 md:px-6 py-2 font-semibold text-xs md:text-sm transition-colors flex-1 sm:flex-auto',
                    activeTab === 'homeowner'
                      ? 'bg-brand text-pruta-green'
                      : 'text-brand'
                  ]"
                >
                  {{ $t('landing.how_it_works.for_homeowner') }}
                </button>
                <button
                  @click="activeTab = 'agent'"
                  :class="[
                    'rounded-full px-3 md:px-4 py-2 font-semibold text-xs md:text-sm transition-colors flex-1 sm:flex-initial',
                    activeTab === 'agent'
                      ? 'bg-brand text-pruta-green'
                      : 'text-brand'
                  ]"
                >
                  {{ $t('landing.how_it_works.for_agent') }}
                </button>
              </div>
            </div>

            <!-- Steps Cards -->
            <div class="px-0 md:px-[60px]">
              <!-- Homeowner Steps -->
              <div
                v-if="activeTab === 'homeowner'"
                class="flex flex-col md:flex-row gap-6 max-w-[80%] md:max-w-[960px] mx-auto"
              >
                <!-- Step 1 -->
                <StepCard
                  :stepNumber="1"
                  :animationData="budgetingAnimation"
                  :title="$t('landing.how_it_works.step1.title')"
                  :description="$t('landing.how_it_works.step1.desc')"
                />

                <!-- Step 2 -->
                <StepCard
                  :stepNumber="2"
                  :animationData="candidateAnimation"
                  :title="$t('landing.how_it_works.step2.title')"
                  :description="$t('landing.how_it_works.step2.desc')"
                />

                <!-- Step 3 -->
                <StepCard
                  :stepNumber="3"
                  :animationData="contractAnimation"
                  :title="$t('landing.how_it_works.step3.title')"
                  :description="$t('landing.how_it_works.step3.desc')"
                />
              </div>

              <!-- Agent Steps -->
              <div
                v-else-if="activeTab === 'agent'"
                class="flex flex-col md:flex-row gap-6 max-w-[80%] md:max-w-[960px] mx-auto"
              >
                <!-- Agent Step 1 -->
                <StepCard
                  :stepNumber="1"
                  :animationData="agentStep1Animation"
                  :title="$t('landing.how_it_works.agent.step1.title')"
                  :description="$t('landing.how_it_works.agent.step1.desc')"
                />

                <!-- Agent Step 2 -->
                <StepCard
                  :stepNumber="2"
                  :animationData="agentStep2Animation"
                  :title="$t('landing.how_it_works.agent.step2.title')"
                  :description="$t('landing.how_it_works.agent.step2.desc')"
                />

                <!-- Agent Step 3 -->
                <StepCard
                  :stepNumber="3"
                  :animationData="agentStep3Animation"
                  :title="$t('landing.how_it_works.agent.step3.title')"
                  :description="$t('landing.how_it_works.agent.step3.desc')"
                />
              </div>
            </div>

            <!-- Decoration Image -->
            <img
              src="/images/decoration-old-woman.svg"
              alt=""
              class="h-[120px] md:h-[160px] w-full"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Why You'll Love Pruta Section -->
    <section class="bg-pruta-green px-4 py-10">
      <div class="w-full max-w-7xl mx-auto  flex flex-col gap-4">
        <div class="flex flex-col items-center gap-4">
          <h2 class="font-medium text-3xl md:text-display-md text-brand">
            {{ $t('landing.why_pruta.title') }}
          </h2>
          <p class="font-medium text-base md:text-lg text-white text-center">
            {{ $t('landing.why_pruta.subtitle') }}
          </p>
        </div>

        <div
          class="justify-center flex flex-col md:flex-row md:flex-wrap gap-6 md:gap-10 py-10"
        >
          <!-- Feature 1 -->
          <div
            class="border border-brand rounded-xl flex gap-2 items-center p-4 md:w-[calc(50%-20px)] lg:w-[504px]"
          >
            <img
              src="/images/transparency.svg"
              alt=""
              class="h-[100px] flex-shrink-0"
            />
            <div class="flex-1 flex flex-col gap-2">
              <h3 class="font-bold text-lg md:text-xl text-brand">
                {{ $t('landing.why_pruta.feature1.title') }}
              </h3>
              <p class="text-sm md:text-md text-white">
                {{ $t('landing.why_pruta.feature1.desc') }}
              </p>
            </div>
          </div>

          <!-- Feature 2 -->
          <div
            class="border border-brand rounded-xl flex gap-2 items-end px-4 md:px-6 py-4 md:w-[calc(50%-20px)] lg:w-[504px]"
          >
            <img
              src="/images/old-user.svg"
              alt=""
              class="h-[100px] flex-shrink-0"
            />
            <div class="flex-1 flex flex-col gap-2">
              <h3 class="font-bold text-lg md:text-xl text-brand">
                {{ $t('landing.why_pruta.feature2.title') }}
              </h3>
              <p class="text-sm md:text-md text-white">
                {{ $t('landing.why_pruta.feature2.desc') }}
              </p>
            </div>
          </div>

          <!-- Feature 3 -->
          <div
            class="border border-brand rounded-xl flex gap-2 items-center p-4 md:w-[calc(50%-20px)] lg:w-[504px]"
          >
            <img
              src="/images/pruta-agent-female.svg"
              alt=""
              class="h-[100px] flex-shrink-0"
            />
            <div class="flex-1 flex flex-col gap-2">
              <h3 class="font-bold text-lg md:text-xl text-brand">
                {{ $t('landing.why_pruta.feature3.title') }}
              </h3>
              <p class="text-sm md:text-md text-white">
                {{ $t('landing.why_pruta.feature3.desc') }}
              </p>
            </div>
          </div>

          <!-- Feature 4 -->
          <div
            class="border border-brand rounded-xl flex gap-2 items-center p-4 md:w-[calc(50%-20px)] lg:w-[504px]"
          >
            <img
              src="/images/pruta-guy-star.svg"
              alt=""
              class="h-[100px] flex-shrink-0"
            />
            <div class="flex-1 flex flex-col gap-2">
              <h3 class="font-bold text-lg md:text-xl text-brand">
                {{ $t('landing.why_pruta.feature4.title') }}
              </h3>
              <p class="text-sm md:text-md text-white">
                {{ $t('landing.why_pruta.feature4.desc') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Image Banner -->
        <div class="flex flex-col gap-6 md:gap-10">
          <div
            class="h-[200px] md:h-[400px] relative overflow-hidden rounded-xl"
          >
            <img
              :src="getLocalizedImage('/images/couple-2.webp')"
              alt=""
              class="absolute w-full h-full object-cover"
            />
            <div class="absolute inset-0 bg-black/20"></div>
          </div>
          <!-- CTA Button -->
          <div class="flex flex-col gap-4 items-center">
            <Button variant="primary" size="md" class="md:px-8 md:py-3 md:text-md"> {{ $t('landing.why_pruta.cta_button') }} </Button>
            <p class="font-semibold text-sm md:text-lg text-white text-center">
              {{ $t('landing.why_pruta.cta_text') }}
            </p>
          </div>
        </div>

      </div>
    </section>

    <!-- NSW Expansion Section -->
    <section class="bg-bg-cream py-10">
      <div
        class="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 flex flex-col md:flex-row gap-8 md:gap-20 items-center py-10"
      >
        <img
          src="/images/aussie-map.svg"
          alt="Australia Map"
          class="w-full max-w-[300px] md:max-w-none md:w-auto"
        />

        <div class="flex flex-col gap-6 py-6 w-full md:w-[540px]">
          <p class="font-bold text-2xl md:text-display-xs text-pruta-green">
            {{ $t('landing.expansion.text') }}
          </p>

          <div
            class="bg-pruta-green flex flex-col gap-6 p-4 md:p-6 rounded-xl shadow-2xl"
          >
            <p class="font-medium text-base md:text-lg text-brand text-center">
              {{ $t('landing.expansion.waitlist_text') }}
            </p>

            <div class="flex flex-col md:flex-row gap-4 md:gap-2 md:items-end">
              <div class="flex-1 flex flex-col gap-2 relative">
                <label class="font-medium text-sm md:text-md text-white">
                  {{ $t('landing.expansion.select_state') }}
                </label>
                <div
                  @click="isStateDropdownOpen = !isStateDropdownOpen"
                  class="bg-white border border-gray-light rounded-md flex items-center cursor-pointer"
                >
                  <p
                    class="flex-1 text-sm md:text-md pl-[14px] py-3"
                    :class="selectedState ? 'text-gray-darker' : 'text-gray-dark'"
                  >
                    {{ selectedState ? selectedState.name : 'Select a state' }}
                  </p>
                  <svg
                    class="w-5 h-5 mr-[14px] transition-transform flex-shrink-0"
                    :class="{ 'rotate-180': isStateDropdownOpen }"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#333333"
                    stroke-width="2"
                  >
                    <path
                      d="M6 9l6 6 6-6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>

                <!-- Dropdown Menu -->
                <div
                  v-if="isStateDropdownOpen"
                  class="absolute top-full mt-1 w-full bg-white border border-gray-light rounded-md shadow-2xl max-h-60 overflow-y-auto z-10"
                >
                  <div
                    v-for="state in australianStates"
                    :key="state.id"
                    @click="selectState(state)"
                    class="px-[14px] py-3 text-sm md:text-md text-gray-darker hover:bg-gray-50 cursor-pointer transition-colors flex items-center justify-between"
                    :class="{ 'bg-gray-100': selectedState?.id === state.id }"
                  >
                    <span>{{ state.name }}</span>
                    <svg
                      v-if="selectedState?.id === state.id"
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        d="M5 13l4 4L19 7"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div class="flex-1 flex flex-col gap-2">
                <label
                  class="capitalize font-medium text-sm md:text-md text-white"
                >
                  {{ $t('landing.expansion.email_label') }}
                </label>
                <div
                  class="bg-white border border-gray-light rounded-md px-[14px]"
                >
                  <input
                    type="email"
                    placeholder=""
                    class="w-full text-sm md:text-md text-gray-darker bg-transparent outline-none py-3"
                  />
                </div>
              </div>
            </div>

            <div class="flex justify-center md:justify-end">
              <Button variant="primary" size="lg"> {{ $t('landing.expansion.join_waitlist') }} </Button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Privacy & Security Section -->
    <section class="bg-pruta-green pb-10">
      <div class="p-4 md:p-6 w-full max-w-7xl mx-auto">
        <div class="h-[200px] md:h-[400px] rounded-xl relative overflow-hidden">
          <img
            src="/images/agent-hero-2.webp"
            alt=""
            class="absolute w-full h-full object-cover"
          />
          <div class="absolute inset-0 bg-black/20"></div>
        </div>
      </div>

      <div class="max-w-7xl mx-auto px-8 md:px-6 lg:px-8 py-8">
        <div
          class="flex flex-col gap-6 md:gap-20"
        >
          <div class="border-b-2 border-pruta-green flex flex-col md:flex-row md:justify-between gap-4 md:gap-2 md:items-end pb-6">
            <h2 class="font-semibold text-2xl sm:text-display-md text-brand sm:w-1/3">
              {{ $t('landing.privacy.title') }}
            </h2>
            <p class="font-medium text-md md:text-lg text-white sm:w-2/3">
              {{ $t('landing.privacy.subtitle') }}
            </p>
          </div>

          <div class="flex flex-col md:flex-row gap-8 md:gap-12">
            <div class="flex-1 flex flex-col gap-2">
              <h3 class="font-semibold text-xl md:text-2xl text-brand">
                {{ $t('landing.privacy.verified_agents.title') }}
              </h3>
              <p class="font-medium text-sm md:text-md text-white">
                {{ $t('landing.privacy.verified_agents.desc') }}
              </p>
            </div>

            <div class="flex-1 flex flex-col gap-2">
              <h3 class="font-semibold text-xl md:text-2xl text-brand">
                {{ $t('landing.privacy.private.title') }}
              </h3>
              <p class="font-medium text-sm md:text-md text-white">
                {{ $t('landing.privacy.private.desc') }}
              </p>
            </div>

            <div class="flex-1 flex flex-col gap-2">
              <h3 class="font-semibold text-xl md:text-2xl text-brand">
                {{ $t('landing.privacy.safe.title') }}
              </h3>
              <p class="font-medium text-sm md:text-md text-white">
                {{ $t('landing.privacy.safe.desc') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="bg-bg-cream py-10">
      <div class="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 flex flex-col gap-6">
        <h2
          class="font-semibold text-3xl md:text-display-md text-pruta-green text-center max-w-[1080px] mx-auto"
        >
          {{ $t('landing.faq.title') }}
        </h2>

        <div class="flex flex-col md:flex-row gap-6 md:gap-10">
          <div class="flex-1">
            <FaqItem
              v-for="faq in faqs"
              :key="faq.id"
              :question="faq.question"
              :answer="faq.answer"
              :default-open="faq.id === 1"
            />
          </div>

          <div
            class="flex-1 h-[300px] md:h-[472px] rounded-xl relative overflow-hidden"
          >
            <img
              :src="getLocalizedImage('/images/2-female.webp')"
              alt=""
              class="absolute w-full h-full object-cover"
            />
            <div class="absolute inset-0 bg-black/20"></div>
          </div>
        </div>
      </div>
    </section>

    <div class="py-8 px-4 md:px-6">
      <PromotionalBanner @cta-click="handleCtaClick" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import budgetingAnimation from "@/public/animation/budgeting.json";
import candidateAnimation from "@/public/animation/candidate.json";
import contractAnimation from "@/public/animation/contract.json";
import agentVerifyAnimation from "@/public/animation/agent-verify.json";
import agentListingAnimation from "@/public/animation/agent-listing.json";
import agentCompeteAnimation from "@/public/animation/agent-compete.json";
import faqData from "@/data/faq.json";
import australianStates from "@/data/australian-states.json";

const { locale } = useI18n();

const faqs = computed(() => {
  return faqData.faqs[locale.value] || faqData.faqs.en;
});

// State dropdown management
const selectedState = ref(null);
const isStateDropdownOpen = ref(false);

const selectState = (state) => {
  selectedState.value = state;
  isStateDropdownOpen.value = false;
};

// Tab state management
const activeTab = ref("homeowner");

// Agent animations
const agentStep1Animation = agentVerifyAnimation;
const agentStep2Animation = agentListingAnimation;
const agentStep3Animation = agentCompeteAnimation;

// Localized images
const { getLocalizedImage } = useLocalizedImage();

const handleCtaClick = () => {
  // Add your CTA click logic here
  console.log("CTA clicked");
};
</script>

<style scoped>
/* Add custom fonts */
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Inter:wght@400;500;600;700&family=Noto+Sans+SC:wght@400;500;600;700&display=swap");

/* Mobile gradient - 0deg for both gradients (default) */
.gradient-overlay {
  background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 43.807%,
      rgba(0, 0, 0, 0.3) 51.584%,
      rgba(0, 0, 0, 0.6) 100%
    ),
    linear-gradient(
      180deg,
      rgba(119, 135, 0, 0.15) 0%,
      rgba(119, 135, 0, 0.15) 100%
    );
}

/* Desktop gradient - original degrees (270deg and 90deg) at md breakpoint */
@media (min-width: 768px) {
  .gradient-overlay {
    background: linear-gradient(
        270deg,
        rgba(0, 0, 0, 0) 43.807%,
        rgba(0, 0, 0, 0.3) 51.584%,
        rgba(0, 0, 0, 0.6) 100%
      ),
      linear-gradient(
        90deg,
        rgba(119, 135, 0, 0.15) 0%,
        rgba(119, 135, 0, 0.15) 100%
      );
  }
}
</style>
